import { useState, useRef } from "react";
import TurnstileWidget, { type TurnstileWidgetRef } from "./TurnstileWidget";
import { submitAccessRequest } from "../../api/accessRequestsApi";

interface RequestAccessCardProps {
  onSwitchToSignIn?: () => void;
  onSwitchToAdminLogin?: () => void;
}

export default function RequestAccessCard({ onSwitchToSignIn, onSwitchToAdminLogin }: RequestAccessCardProps) {
  const handleSwitchToSignIn = onSwitchToSignIn || onSwitchToAdminLogin;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [reason, setReason] = useState("");
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Client-side validations with accessible messages
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError("Please provide your full legal or professional name (minimum 2 characters).");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid work email address.");
      return;
    }
    if (!company.trim()) {
      setError("Please provide your organization or company name.");
      return;
    }
    if (!role.trim()) {
      setError("Please specify your current job title or security function.");
      return;
    }
    if (!reason.trim() || reason.trim().length < 10) {
      setError("Please explain your reason for requesting access (minimum 10 characters).");
      return;
    }

    setLoading(true);

    try {
      const res = await submitAccessRequest({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        company: company.trim(),
        role: role.trim(),
        reason: reason.trim(),
        website: website.trim() || undefined,
        turnstile_token: turnstileToken || undefined,
      });

      setSubmitted(true);
      setSuccessMessage(res.message);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          status?: number;
          data?: {
            detail?: string;
          };
        };
        message?: string;
      };
      const detail = axiosErr.response?.data?.detail;
      setError(detail || axiosErr.message || "Unable to submit access request. Please try again.");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFullName("");
    setEmail("");
    setCompany("");
    setRole("");
    setReason("");
    setWebsite("");
    setTurnstileToken("");
    setSubmitted(false);
    setError("");
  };

  if (submitted) {
    return (
      <div className="tx-auth-card-body" id="access-request-confirmed-state">
        <div style={{ textAlign: "center", padding: "16px 0 24px 0" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(16, 185, 129, 0.12)",
              border: "1px solid rgba(16, 185, 129, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
              color: "#10b981",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>

          <h2 className="tx-auth-form-title" style={{ marginBottom: 10 }}>
            Request Received
          </h2>
          <p className="tx-auth-form-subtitle" style={{ maxWidth: 420, margin: "0 auto 24px auto", lineHeight: "1.6" }}>
            {successMessage}
          </p>

          <div
            style={{
              background: "#141922",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 8,
              padding: 18,
              marginBottom: 28,
              textAlign: "left",
              fontSize: 13,
              color: "#94a3b8",
              lineHeight: "1.5",
            }}
          >
            <div style={{ fontWeight: 600, color: "#f8fafc", marginBottom: 6 }}>
              What happens next?
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: "#cbd5e1" }}>
              <li style={{ marginBottom: 6 }}>
                Our intelligence access team reviews each application individually.
              </li>
              <li style={{ marginBottom: 6 }}>
                If approved, a secure single-use invitation link will be delivered to <strong>{email}</strong>.
              </li>
              <li>
                Requesting access does not immediately grant platform authorization.
              </li>
            </ul>
          </div>

          <button
            type="button"
            className="tx-auth-btn-secondary"
            onClick={handleReset}
            style={{
              width: "100%",
              padding: "12px 20px",
              background: "#161b24",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: 8,
              color: "#f8fafc",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Submit Another Request
          </button>
          {handleSwitchToSignIn && (
            <button
              type="button"
              onClick={handleSwitchToSignIn}
              style={{
                width: "100%",
                padding: "12px 20px",
                background: "rgba(16, 185, 129, 0.15)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                borderRadius: 8,
                color: "#10b981",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Return to Sign In &rarr;
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tx-auth-card-body" id="access-request-form-container">
      {/* Badge */}
      <div style={{ marginBottom: 12 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 9999,
            background: "rgba(16, 185, 129, 0.12)",
            border: "1px solid rgba(16, 185, 129, 0.3)",
            color: "#10b981",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981" }} />
          Pre-Launch Early Access
        </span>
      </div>

      <h2 className="tx-auth-form-title" style={{ marginBottom: 6 }}>
        Request Access to Threatactix Intelligence
      </h2>
      <p className="tx-auth-form-subtitle" style={{ marginBottom: 24, lineHeight: "1.5" }}>
        Threatactix is preparing for official launch. Submit your information below to request vetted access to darknet and cyber threat intelligence feeds.
      </p>

      {error && (
        <div className="tx-auth-alert tx-auth-alert-error" role="alert" style={{ marginBottom: 20 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form className="tx-auth-form" onSubmit={handleSubmit} noValidate>
        {/* Full Name */}
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-req-name">
            Full Name <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-req-name"
              type="text"
              required
              placeholder="e.g. Sarah Jenkins"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
              maxLength={100}
            />
          </div>
        </div>

        {/* Work Email */}
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-req-email">
            Work Email <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-req-email"
              type="email"
              required
              placeholder="sarah.jenkins@enterprise.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
              maxLength={120}
            />
          </div>
        </div>

        {/* Organization / Company */}
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-req-company">
            Company or Organization <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-req-company"
              type="text"
              required
              placeholder="e.g. Apex Cyber Systems"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
              maxLength={100}
            />
          </div>
        </div>

        {/* Role or Designation */}
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-req-role">
            Job Role / Title <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-req-role"
              type="text"
              required
              placeholder="e.g. Head of Threat Intelligence / CISO"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
              maxLength={100}
            />
          </div>
        </div>

        {/* Reason for Requesting Access */}
        <div className="tx-auth-field">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <label className="tx-auth-label" htmlFor="tx-req-reason">
              Reason for Requesting Access <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <span style={{ fontSize: 11, color: reason.length > 450 ? "#ef4444" : "#64748b" }}>
              {reason.length}/500
            </span>
          </div>
          <div className="tx-auth-input-wrapper">
            <textarea
              id="tx-req-reason"
              required
              rows={3}
              placeholder="Briefly describe your intelligence use cases, brand protection requirements, or darknet monitoring scope..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="tx-auth-input"
              style={{ resize: "vertical", minHeight: 76, padding: "10px 14px", height: "auto" }}
              disabled={loading}
              maxLength={500}
            />
          </div>
        </div>

        {/* Optional Business Website */}
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-req-website">
            Company Website <span style={{ fontSize: 11, color: "#64748b" }}>(optional)</span>
          </label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-req-website"
              type="url"
              placeholder="https://enterprise.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
              maxLength={200}
            />
          </div>
        </div>

        {/* Privacy Notice */}
        <p style={{ fontSize: 12, lineHeight: "17px", color: "#64748b", margin: "6px 0 14px 0" }}>
          🔒 Privacy Notice: Information submitted is encrypted and reviewed strictly for access eligibility. Threatactix does not share applicant information.
        </p>

        {/* Turnstile Bot Protection */}
        <TurnstileWidget
          ref={turnstileRef}
          onSuccess={(tok) => setTurnstileToken(tok)}
          onError={() => setTurnstileToken("")}
          onExpire={() => setTurnstileToken("")}
        />

        {/* Submit Button */}
        <button
          type="submit"
          className="tx-auth-btn-primary"
          disabled={loading}
          id="btn-submit-access-request"
          style={{ marginTop: 8 }}
        >
          {loading ? (
            <>
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              <span>Submitting Request...</span>
            </>
          ) : (
            "Request Access"
          )}
        </button>

        {/* Return to Sign-In link */}
        {handleSwitchToSignIn && (
          <div style={{ textAlign: "center", marginTop: 24, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ fontSize: 13, color: "var(--tx-auth-text-muted)" }}>
              Already have an account or invitation?
            </span>{" "}
            <button
              type="button"
              id="btn-return-to-sign-in"
              onClick={handleSwitchToSignIn}
              style={{
                background: "none",
                border: "none",
                color: "#10b981",
                fontSize: 13,
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Sign In &rarr;
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
